/* eslint-disable object-curly-spacing */
/* eslint-disable no-undef */
/* eslint-disable own-handles */
/* eslint-disable no-innerhtml-assign */
/* eslint-disable consistent-method-names */
/* eslint-disable no-jsdoc */
/* eslint-disable max-len */
/**
 * Allows to make search request using the TomTom's
 * [Search API - Along Route Search](https://developer.tomtom.com/search-api/search-api-documentation-search/along-route-search).
 *
 * The Along Route Search endpoint allows you to perform a fuzzy search for
 * POIs along a specified route. This search is constrained by specifying
 * Detour Time limiting measure.
 *
 * To send the route points this service will use a POST request with the {{#crossLink "AlongRouteSearch/route:method"}}route{{/crossLink}} encoded as a JSON payload.
 * The minimum number of route points is 2.
 *
 * @example
 *```js
 * tomtom.alongRouteSearch({
 *     limit: 20,
 *     maxDetourTime: 120,
 *     query: 'gas station',
 *     route: [
 *         {
 *             "lat": 37.7524152343544,
 *             "lon":-122.43576049804686
 *         },
 *         {
 *             "lat": 37.70660472542312,
 *             "lon":-122.43301391601562
 *         },
 *         [37.712059855877314, -122.36434936523438], // Another valid format
 *         new L.LatLng(37.75350561243041, -122.37396240234374) // Leaflet's L.LatLng instances are also allowed
 *     ]
 *   })
 *   .go()
 *   .then(function(response) {
 *     console.log('SUMMARY:');
 *     console.table(response.summary);
 *     console.log('RESULTS:');
 *     console.table(response.results);
 *   });
 * ```
 *
 * For a list of all available format for routes read the documentation for the
 * {{#crossLink "AlongRouteSearch/route:method"}}{{/crossLink}} option.
 *
 * @class AlongRouteSearch
 * @module Services
 * @uses Chain
 * @uses KeyMixin
 * @uses LimitMixin
 * @uses ViewMixin
 * @uses QueryMixin
 * @constructor
 *
 * @param {Object} [options]
 * @param {String} [options.key] An API Key issued from <a href="https://developer.tomtom.com">Developer Portal</a>.
 * @param {Number} [options.limit=10] Maximum number of responses that should be returned. Maximum number is 20.
 * @param {String} [options.view="Unified"] Geopolitical view
 * @param {String} [options.maxDetourTime] Maximum detour time in seconds.
 *     This option is mandatory. For that reason needs to be defined during the instance
 *     construction or using the <a href="#method_maxDetourTime">maxDetourTime()</a> setter function.
 *     The maximum allowed value is 3600.
 * @param {String} options.query Text that will be searched.
 *     This option is mandatory. For that reason needs to be defined during the instance
 *     construction or using the <a href="#method_query">query()</a> setter function.
 * @param {Array} options.route The route representation.
 *     This option is mandatory. For that reason needs to be defined during the instance
 *     construction or using the <a href="#method_route">route()</a> setter function.
 *     <a href="#method_route">See more details</a>.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 */
/* eslint-enable max-len */
define([
    '../chain',
    '../converters',
    '../validators',
    'rest/search',
    'common/searchTypes',
    'common/parameterApplications'
], function(chain, converters, validators, restSearch, searchTypes, parameterApplications) {
    'use strict';

    var searchType = searchTypes.searchAlongRoute;
    var fields = {
        key: {validator: validators.string},
        /**
         * Sets or gets the maximum detour time in seconds.
         *
         * The maximum allowed value is 3600.
         *
         * @method maxDetourTime
         * @chainable
         * @param {Number} [value] New maximum detour time in seconds.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {Number} The same service instance or the current option value if no argument was given.
         */
        maxDetourTime: { validator: validators.integerInInterval(0, 3600) },

        /**
         * Sets or gets the route to search along. The route **should have at least two points**.
         *
         * The following formats are supported:
         *
         * - **String[]**
         *   - `["52.3,4.8", "52.3,4.8"]`
         * - **Array[]**
         *   - `[ [52.3, 4.8], [52.3, 4.8] ]`
         * - **Object[]**
         *   - `{lat: 52.3, lon: 4.8}`
         *   - `{lat: 53.3, lng: 5.8}`
         *   - `{x: 53.3, y: 5.8}`
         *   - `{latitude: 53.3, longitude: 5.8}`
         *   - `{lat: Function, lng: Function}` The functions should return a numeric value
         *
         * @example
         *```js
         * tomtom.alongRouteSearch()
         *   .route([
         *     {
         *         lat: 37.7524152343544,
         *         lon:-122.43576049804686
         *     },
         *     {
         *         lat: 37.70660472542312,
         *         lon:-122.43301391601562
         *     }
         *   ])
         *   .go(callback);
         *```
         *
         * @method route
         * @chainable
         * @param {Array} [route] Route representation.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {Array} The same service instance or the current option value if no argument was given.
         */
        route: {
            required: true,
            converter: converters.route,
            validator: validators.route,
            application: parameterApplications.POST
        },

        query: {
            required: true,
            validator: validators.string,
            converter: converters.queryString,
            application: parameterApplications.PATH
        },

        // A custom "limit" docblock is used because the max limit value differs from the used on the
        // other services (100).
        /**
         * Sets or gets the limit of the elements for the response.
         *
         * @method limit
         * @chainable
         * @param {Number} [limit] Maximum number of the elements in response. Maximum number is 20.
         * @throws {TypeError} If the given argument cannot be converted to a valid value.
         * @return {Number} The same service instance or the current option value if no argument was given.
         */
        limit: {
            validator: validators.integerInInterval(0, 20)
        },
        type: {
            validator: validators.string,
            defaultValue: searchType,
            application: parameterApplications.PATH
        }
    };

    var batchFields = {
        batchItems: {
            validator: validators.arrayOf(fields),
            converter: converters.arrayOf(fields)
        }
    };

    var search = restSearch();

    function handleChain(data, callback) {
        data.type = searchType;
        search.search(fields, data, function(response) {
            callback.call(this, response);
        });
    }

    function handleBatchChain(data, callback) {
        data.type = searchType;
        search.batch(fields, data, function(response) {
            callback.call(this, response);
        });
    }

    return function(options) {
        if (Array.isArray(options)) {
            return chain(batchFields, handleBatchChain, {
                batchItems: options
            });
        } else {
            return chain(fields, handleChain, options);
        }
    };
});



// WEBPACK FOOTER //
// ./services/search/alongRouteSearch.js